import React, { useState } from "react";
import Container from "../components/ui/Container/Container";
import PageHeading from "../components/ui/PageHeading/PageHeading";
import { useStationAvailability } from "../hooks/useStationAvailability";
import Input, { EInputType } from "../components/ui/Input/Input";
import { format, endOfMonth } from 'date-fns';
import { useStationOptions } from "../hooks/useStationOptions";

const AvailabilityDebugPage: React.FC = () => {
  const [station, setStation] = useState('HEL');
  const [yearMonth, setYearMonth] = useState(format(new Date(), 'yyyy-MM'));
  
  const { data, isFetching } = useStationAvailability(station, yearMonth);
  const { pickupOptions, loading: stationsLoading } = useStationOptions();
  
  let daysInMonth = 31;
  try {
    daysInMonth = endOfMonth(new Date(yearMonth + '-01')).getDate();
  } catch (e) {
    // ignore invalid date formatting temporarily
  }
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const isMemcacheReady = data && !data.fetching && Object.keys(data.data || {}).length > 0;

  return (
    <>
      <PageHeading>Availability Debug</PageHeading>
      <Container>
        <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
          Tämä näkymä kysyy backendin <code>/json/availability</code> rajapintaa. Jos dataa ei ole, backend liipaisee Task Queue -jonoon haun.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem', maxWidth: '500px' }}>
          <Input 
            inputName="debugStation"
            type={EInputType.reactSelect} 
            label="Station" 
            value={station} 
            options={pickupOptions}
            loading={stationsLoading}
            onChange={(v) => setStation(v as string)} 
          />
          <Input 
            inputName="debugYearMonth"
            type={EInputType.text} 
            label="Year-Month (YYYY-MM)" 
            value={yearMonth} 
            onChange={(v) => setYearMonth(v as string)} 
          />
        </div>
        
        {isFetching && <p style={{ color: '#666', fontStyle: 'italic' }}>Ladataan rajapinnasta...</p>}
        {data?.fetching && <p style={{ color: '#e67e22', fontWeight: 'bold' }}>Data puuttui Memcachesta. Task Queue hakee taustalla (kysellään tuloksia 5s välein)...</p>}
        
        {data && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem', background: '#fff', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: '#eee' }}>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Date</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Available Categories</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Data Source</th>
                </tr>
              </thead>
              <tbody>
                {days.map(day => {
                  const dayStr = `${yearMonth}-${day.toString().padStart(2, '0')}`;
                  const dayData = data.data?.[dayStr];
                  
                  const rowStyle = { 
                    backgroundColor: isMemcacheReady ? '#e6ffe6' : 'transparent',
                    transition: 'background-color 0.5s'
                  };

                  return (
                    <tr key={dayStr} style={rowStyle}>
                      <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>{dayStr}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        {dayData ? (
                          <div>
                            <div style={{ 
                              color: dayData.availableCount > 0 ? 'green' : 'red', 
                              fontWeight: 'bold',
                              marginBottom: dayData.availableCategories ? '4px' : '0'
                            }}>
                              {dayData.availableCount} kpl
                            </div>
                            {dayData.availableCategories && Object.keys(dayData.availableCategories).length > 0 && (
                              <div style={{ fontSize: '0.8rem', color: '#555' }}>
                                {Object.entries(dayData.availableCategories)
                                  .sort(([a], [b]) => a.localeCompare(b))
                                  .map(([code, available], index, array) => (
                                    <span key={code}>
                                      <span style={{ color: available === 1 ? 'green' : 'inherit' }}>
                                        {code}: {available}
                                      </span>
                                      {index < array.length - 1 ? ', ' : ''}
                                    </span>
                                  ))}
                              </div>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: '0.8rem', color: '#666' }}>
                        {isMemcacheReady ? '✅ Memcache' : (data.fetching ? '⏳ Odottaa Task Queuea...' : 'Tyhjä')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Container>
    </>
  );
};

export default AvailabilityDebugPage;
